import React from "react";
import { Link } from "react-router-dom";
import { Section } from "../components/SectionTitle";
import ConsultationForm from "../components/ConsultationForm";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { Check, ArrowRight } from "lucide-react";
import { CORE_SERVICES } from "../lib/serviceData";

export default function ServiceDetailPage({ slug, heroImage, heroSubtitle, longDescription, complianceNote }) {
  const svc = CORE_SERVICES.find((s) => s.slug === slug);
  if (!svc) return null;
  const Icon = svc.icon;

  return (
    <div>
      <SEO
        title={svc.title}
        description={heroSubtitle || svc.description}
        keywords={`${svc.title.toLowerCase()} USA, ${svc.points.slice(0, 3).join(", ").toLowerCase()}`}
        path={`/services/${slug}`}
      />
      <section className="relative bg-[#0a2540] text-white overflow-hidden">
        {heroImage && (
          <div className="absolute inset-0">
            <img src={heroImage} alt="" className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540] via-[#0a2540]/80 to-[#0a2540]/30" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 flex items-center justify-center text-[#d4af37]">
                <Icon className="w-6 h-6" strokeWidth={1.6} />
              </div>
              <span className="eyebrow text-[#d4af37]">{svc.title}</span>
            </div>
            <h1 className="font-serif-hsa text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
              {svc.title.replace(" Services", "")} Support, Coordinated Under One Umbrella
            </h1>
            <p className="text-lg text-white/85 max-w-2xl">
              {heroSubtitle || svc.description}
            </p>
          </div>
        </div>
      </section>

      <Section className="bg-white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">What We Cover</div>
            <h2 className="font-serif-hsa text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-6 leading-tight">
              Coordinated {svc.title.toLowerCase()} for individuals, families, and businesses
            </h2>
            <p className="text-slate-700 leading-relaxed mb-8">
              {longDescription || svc.description}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-10">
              {svc.points.map((p) => (
                <div key={p} className="flex items-start gap-3 bg-[#f8f9fa] border border-[#cbd5e1] rounded-lg p-4">
                  <Check className="w-5 h-5 text-[#1e6dd8] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-800 font-medium text-sm">{p}</span>
                </div>
              ))}
            </div>

            {complianceNote && (
              <div className="border-l-4 border-[#d4af37] bg-[#fdf8eb] rounded-r-lg p-5 text-sm text-slate-700 leading-relaxed">
                <strong className="text-[#0a2540]">Important:</strong> {complianceNote}
              </div>
            )}

            <Link to="/contact" className="inline-block mt-10">
              <Button data-testid={`service-detail-cta-${slug}`} className="bg-[#0a2540] hover:bg-[#1e6dd8] text-white rounded-full px-7 h-12">
                Book a Consultation <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <ConsultationForm compact />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
